export type InboxItemKind =
  | "REQUISITION_APPROVAL"
  | "REQUISITION_EXCEPTION_REASON"
  | "FORECAST_SUBMISSION";

export type InboxCategory =
  | "EVERYTHING"
  | "APPROVALS"
  | "EXCEPTION_REASONS"
  | "FORECASTS";

export type InboxCategoryCount = {
  category: InboxCategory;
  count: number;
};

export type ParsedInboxKey = {
  kind: InboxItemKind;
  subjectId: string;
  qualifier: string;
};

const SEP = "~";

/** encodeURIComponent leaves `~` unreserved; it is our separator. */
const encodePart = (value: string): string =>
  encodeURIComponent(value).replaceAll("~", "%7E");

export const isInboxItemKind = (value: string): value is InboxItemKind =>
  value === "REQUISITION_APPROVAL" ||
  value === "REQUISITION_EXCEPTION_REASON" ||
  value === "FORECAST_SUBMISSION";

export const inboxItemKey = (
  kind: InboxItemKind,
  subjectId: string,
  qualifier: string
): string =>
  `${kind}${SEP}${encodePart(subjectId)}${SEP}${encodePart(qualifier)}`;

export const parseInboxItemKey = (key: string): ParsedInboxKey | null => {
  const parts = key.split(SEP);
  const kind = parts[0] ?? "";
  if (parts.length !== 3 || !isInboxItemKind(kind)) {
    return null;
  }
  try {
    const subjectId = decodeURIComponent(parts[1] ?? "");
    const qualifier = decodeURIComponent(parts[2] ?? "");
    if (subjectId === "" || qualifier === "") {
      return null;
    }
    return { kind, subjectId, qualifier };
  } catch {
    return null;
  }
};

export const categoryOf = (
  kind: InboxItemKind
): Exclude<InboxCategory, "EVERYTHING"> => {
  switch (kind) {
    case "REQUISITION_APPROVAL":
      return "APPROVALS";
    case "REQUISITION_EXCEPTION_REASON":
      return "EXCEPTION_REASONS";
    case "FORECAST_SUBMISSION":
      return "FORECASTS";
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

export const INBOX_FILTERS = [
  { value: "EVERYTHING", label: "Everything" },
  { value: "APPROVALS", label: "Approvals" },
  { value: "EXCEPTION_REASONS", label: "Exception reasons" },
  { value: "FORECASTS", label: "Forecasts" },
] as const satisfies readonly { value: InboxCategory; label: string }[];

const KIND_PARAM = "kind";

export const isInboxCategory = (value: string): value is InboxCategory =>
  INBOX_FILTERS.some((item) => item.value === value);

export const categoryFromSearch = (search: string): InboxCategory => {
  const value = new URLSearchParams(search).get(KIND_PARAM);
  return value && isInboxCategory(value) ? value : "EVERYTHING";
};

export const withCategorySearch = (category: InboxCategory): string =>
  category === "EVERYTHING" ? "" : `?${KIND_PARAM}=${category}`;

export const inboxBadgeTotal = (
  counts: readonly { category: string; count: number }[]
): number => {
  const everything = counts.find((row) => row.category === "EVERYTHING");
  if (everything) {
    return everything.count;
  }
  return counts.reduce((sum, row) => sum + row.count, 0);
};

export const countForCategory = (
  counts: readonly { category: string; count: number }[],
  category: string
): number => {
  if (category === "EVERYTHING") {
    return inboxBadgeTotal(counts);
  }
  return counts.find((row) => row.category === category)?.count ?? 0;
};

export const itemInCategory = (
  kind: InboxItemKind,
  category: InboxCategory
): boolean => category === "EVERYTHING" || categoryOf(kind) === category;

export const otherCategoryCount = (
  counts: readonly InboxCategoryCount[],
  category: InboxCategory
): number => {
  if (category === "EVERYTHING") {
    return 0;
  }
  return counts
    .filter((row) => row.category !== category && row.category !== "EVERYTHING")
    .reduce((sum, row) => sum + row.count, 0);
};
